import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { isContactFormLead } from "../utils/leadHelpers";
import { Heading } from "./heading";
import { Badge } from "./badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "./table";
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const WINDOWS = [
  { key: "7", label: "7 días", days: 7 },
  { key: "30", label: "30 días", days: 30 },
  { key: "60", label: "60 días", days: 60 },
];

const PIPELINE_EXCLUDED_STATUSES = new Set(["ganado", "perdido"]);

const toJsDate = (val) => {
  if (val == null) return null;
  if (val instanceof Date) return val;
  if (typeof val.toDate === "function") return val.toDate();
  if (typeof val.seconds === "number") return new Date(val.seconds * 1000);
  if (typeof val === "string" || typeof val === "number") return new Date(val);
  return null;
};

const fmtDateTime = (val) => {
  const d = toJsDate(val);
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtRelative = (val) => {
  const d = toJsDate(val);
  if (!d || isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `hace ${days} días`;
  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} ${months === 1 ? "mes" : "meses"}`;
  const years = Math.floor(months / 12);
  return `hace ${years} ${years === 1 ? "año" : "años"}`;
};

// Same as fmtRelative but never collapses to months/years — always shows in
// days (or "hoy" / "ayer"). Use when "1 m" would be ambiguous with minutes.
const fmtRelativeDays = (val) => {
  const d = toJsDate(val);
  if (!d || isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return "hoy";
  if (days === 1) return "ayer";
  return `hace ${days} días`;
};

const SORTS = {
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  bitacora: (a, b) => b.bitacoraCount - a.bitacoraCount,
  social: (a, b) => b.socialCount - a.socialCount,
  pipeline: (a, b) => b.pipelineCount - a.pipelineCount,
  contactForm: (a, b) => b.contactFormCount - a.contactFormCount,
  visits: (a, b) => b.visitsCount - a.visitsCount,
  contactRate: (a, b) => (b.contactRate ?? -1) - (a.contactRate ?? -1),
  presence: (a, b) => b.presenceHours - a.presenceHours,
  lastSeen: (a, b) => {
    const ax = toJsDate(a.lastSeen)?.getTime() || 0;
    const bx = toJsDate(b.lastSeen)?.getTime() || 0;
    return bx - ax;
  },
};

// `embebido`: vive como reporte dentro de Reportes admin, que ya pone el
// título y el encabezado. Suelto conserva su cromo de página.
const AdminStats = ({ embebido = false }) => {
  const [windowKey, setWindowKey] = useState("30");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [sortKey, setSortKey] = useState("presence");
  const [refreshTick, setRefreshTick] = useState(0);

  const windowDays = WINDOWS.find((w) => w.key === windowKey)?.days ?? 30;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const sinceTs = Timestamp.fromDate(
          new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000)
        );

        const venuesSnap = await getDocs(collection(db, "venues"));
        const venues = venuesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const bitacoraQ = query(
          collectionGroup(db, "bitacora"),
          where("timestamp", ">=", sinceTs)
        );
        const socialQ = query(
          collectionGroup(db, "socialClicks"),
          where("timestamp", ">=", sinceTs)
        );
        // Visitas por venue (denominador del % de contacto). Requiere la
        // regla collection-group de loads en firestore.rules y el índice de
        // grupo sobre timestamp (Firestore da el link si falta).
        const loadsQ = query(
          collectionGroup(db, "loads"),
          where("timestamp", ">=", sinceTs)
        );
        const presenceQ = query(
          collection(db, "userActivity"),
          where("lastSeen", ">=", sinceTs)
        );

        const [bitacoraSnap, socialSnap, leadsSnap, presenceSnap, loadsSnap] = await Promise.all([
          getDocs(bitacoraQ),
          getDocs(socialQ),
          getDocs(collection(db, "venueContactForms")),
          getDocs(presenceQ),
          getDocs(loadsQ),
        ]);

        if (cancelled) return;

        // Group visits (loads) by parent venue id.
        const visitsByVenue = new Map();
        loadsSnap.docs.forEach((d) => {
          const venueId = d.ref.parent.parent?.id;
          if (!venueId) return;
          visitsByVenue.set(venueId, (visitsByVenue.get(venueId) || 0) + 1);
        });

        // Group bitácora by parent venue id.
        const bitByVenue = new Map();
        bitacoraSnap.docs.forEach((d) => {
          const venueId = d.ref.parent.parent?.id;
          if (!venueId) return;
          bitByVenue.set(venueId, (bitByVenue.get(venueId) || 0) + 1);
        });

        // Group social clicks by parent venue id, track last click date.
        const socialCountByVenue = new Map();
        const socialLastByVenue = new Map();
        socialSnap.docs.forEach((d) => {
          const venueId = d.ref.parent.parent?.id;
          if (!venueId) return;
          socialCountByVenue.set(venueId, (socialCountByVenue.get(venueId) || 0) + 1);
          const ts = toJsDate(d.data().timestamp);
          if (ts) {
            const prev = socialLastByVenue.get(venueId);
            if (!prev || ts > prev) socialLastByVenue.set(venueId, ts);
          }
        });

        // Leads in pipeline by venueId (status not in won/lost) — all-time
        // snapshot. Contact-form leads counted separately, scoped to the
        // selected time window via createdAt.
        const pipelineByVenue = new Map();
        const contactFormByVenue = new Map();
        const sinceMs = sinceTs.toDate().getTime();
        leadsSnap.docs.forEach((d) => {
          const data = d.data();
          if (!data.venueId) return;
          const status = data.status || "pendiente";
          if (!PIPELINE_EXCLUDED_STATUSES.has(status)) {
            pipelineByVenue.set(data.venueId, (pipelineByVenue.get(data.venueId) || 0) + 1);
          }
          if (isContactFormLead(data.origin)) {
            const created = toJsDate(data.createdAt);
            if (created && created.getTime() >= sinceMs) {
              contactFormByVenue.set(
                data.venueId,
                (contactFormByVenue.get(data.venueId) || 0) + 1
              );
            }
          }
        });

        // Presence: distinct (user, hour-bucket) docs per venueId. Each doc is
        // already one hour of activity (idempotent within the hour), so the
        // count = active hours.
        const presenceByVenue = new Map();
        const presenceLastByVenue = new Map();
        const presenceUsersByVenue = new Map();
        presenceSnap.docs.forEach((d) => {
          const data = d.data();
          const venueId = data.venueId;
          if (!venueId) return;
          presenceByVenue.set(venueId, (presenceByVenue.get(venueId) || 0) + 1);
          const ts = toJsDate(data.lastSeen);
          if (ts) {
            const prev = presenceLastByVenue.get(venueId);
            if (!prev || ts > prev) presenceLastByVenue.set(venueId, ts);
          }
          if (data.userId) {
            if (!presenceUsersByVenue.has(venueId))
              presenceUsersByVenue.set(venueId, new Set());
            presenceUsersByVenue.get(venueId).add(data.userId);
          }
        });

        const built = venues.map((v) => ({
          id: v.id,
          name: v.name || v.id,
          isActive: !!v.isActive,
          isActiveAdmin: !!v.isActiveAdmin,
          bitacoraCount: bitByVenue.get(v.id) || 0,
          socialCount: socialCountByVenue.get(v.id) || 0,
          socialLast: socialLastByVenue.get(v.id) || null,
          pipelineCount: pipelineByVenue.get(v.id) || 0,
          contactFormCount: contactFormByVenue.get(v.id) || 0,
          visitsCount: visitsByVenue.get(v.id) || 0,
          // Contacto = formularios web + clics a redes (misma fórmula que la
          // pestaña Analíticas web del Home).
          contactRate: (visitsByVenue.get(v.id) || 0) > 0
            ? (((contactFormByVenue.get(v.id) || 0) + (socialCountByVenue.get(v.id) || 0)) /
                visitsByVenue.get(v.id)) * 100
            : null,
          presenceHours: presenceByVenue.get(v.id) || 0,
          presenceUsers: (presenceUsersByVenue.get(v.id) || new Set()).size,
          lastSeen: presenceLastByVenue.get(v.id) || null,
        }));

        setRows(built);
      } catch (err) {
        console.error("AdminStats fetch failed:", err);
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [windowDays, refreshTick]);

  const sortedRows = useMemo(() => {
    const cmp = SORTS[sortKey] || SORTS.presence;
    return [...rows].sort(cmp);
  }, [rows, sortKey]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        venues: acc.venues + 1,
        active: acc.active + (r.isActive ? 1 : 0),
        bitacora: acc.bitacora + r.bitacoraCount,
        social: acc.social + r.socialCount,
        pipeline: acc.pipeline + r.pipelineCount,
        contactForm: acc.contactForm + r.contactFormCount,
        visits: acc.visits + r.visitsCount,
        presence: acc.presence + r.presenceHours,
        silentVenues: acc.silentVenues + (r.presenceHours === 0 && r.bitacoraCount === 0 ? 1 : 0),
      }),
      { venues: 0, active: 0, bitacora: 0, social: 0, pipeline: 0, contactForm: 0, visits: 0, presence: 0, silentVenues: 0 }
    );
  }, [rows]);

  const Header = ({ k, label, align = "left" }) => (
    <TableHeader>
      <button
        type="button"
        onClick={() => setSortKey(k)}
        className={`flex w-full items-center gap-1 ${
          align === "right" ? "justify-end" : ""
        } ${sortKey === k ? "font-semibold text-zinc-900 dark:text-white" : "text-zinc-500"}`}
      >
        {label}
        {sortKey === k && <span className="text-[10px]">▼</span>}
      </button>
    </TableHeader>
  );

  return (
    <div className={embebido ? "" : "max-w-7xl mx-auto"}>
      {!embebido && (
        <>
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300/60 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-700/40">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            Vista de administrador
          </div>
          <div className="flex items-center gap-3 mb-1">
            <ChartBarIcon className="h-6 w-6" style={{ color: ACCENT }} />
            <Heading level={1} className="text-xl sm:text-2xl">
              Admin Stats
            </Heading>
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
            Solo visible para administradores. Actividad por venue: cambios
            registrados en bitácora, clicks de redes sociales, leads en
            pipeline y horas de presencia de los usuarios.
          </p>
        </>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500 mr-1">
          Ventana:
        </span>
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            type="button"
            onClick={() => setWindowKey(w.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              windowKey === w.key
                ? "bg-[#1b4d2e] text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
            }`}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setRefreshTick((t) => t + 1)}
          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          title="Recargar"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Recargar
        </button>
      </div>

      {/* Totals */}
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Badge color="zinc">
          <BuildingOffice2Icon className="h-3.5 w-3.5" />
          Venues: {totals.venues}
        </Badge>
        <Badge color="green">Activos: {totals.active}</Badge>
        <Badge color="zinc">Bitácora: {totals.bitacora}</Badge>
        <Badge color="zinc">Social: {totals.social}</Badge>
        <Badge color="zinc">Pipeline: {totals.pipeline}</Badge>
        <Badge color="zinc">Contact form: {totals.contactForm}</Badge>
        <Badge color="zinc">Visitas: {totals.visits}</Badge>
        <Badge color="zinc">
          % contacto:{" "}
          {totals.visits > 0
            ? `${(((totals.contactForm + totals.social) / totals.visits) * 100).toFixed(1)}%`
            : "—"}
        </Badge>
        <Badge color="zinc">Horas presencia: {totals.presence}</Badge>
        <Badge color={totals.silentVenues > 0 ? "red" : "zinc"}>
          Sin actividad: {totals.silentVenues}
        </Badge>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <Table striped>
          <TableHead>
            <TableRow>
              <Header k="name" label="Venue" />
              <Header k="bitacora" label="Bitácora" align="right" />
              <Header k="social" label="Social" align="right" />
              <Header k="pipeline" label="Pipeline" align="right" />
              <Header k="contactForm" label="Contact form" align="right" />
              <Header k="visits" label="Visitas" align="right" />
              <Header k="contactRate" label="% contacto" align="right" />
              <Header k="presence" label="Horas activos" align="right" />
              <Header k="lastSeen" label="Última presencia" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-zinc-500 py-6">
                  Cargando…
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-zinc-500 py-6">
                  Sin venues.
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span>{r.name}</span>
                      {!r.isActive && <Badge color="zinc">Inactivo</Badge>}
                      {r.isActive && !r.isActiveAdmin && (
                        <Badge color="amber">Admin off</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.bitacoraCount}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="flex flex-col items-end">
                      <span>{r.socialCount}</span>
                      {r.socialLast && (
                        <span className="text-[10px] text-zinc-400">
                          {fmtRelativeDays(r.socialLast)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.pipelineCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contactFormCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.visitsCount}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.contactRate == null ? (
                      <span className="text-zinc-400">—</span>
                    ) : (
                      `${r.contactRate.toFixed(1)}%`
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="flex flex-col items-end">
                      <span>{r.presenceHours}</span>
                      {r.presenceUsers > 0 && (
                        <span className="text-[10px] text-zinc-400">
                          {r.presenceUsers} {r.presenceUsers === 1 ? "usuario" : "usuarios"}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.lastSeen ? (
                      <div className="flex flex-col">
                        <span>{fmtRelative(r.lastSeen)}</span>
                        <span className="text-[10px] text-zinc-400">{fmtDateTime(r.lastSeen)}</span>
                      </div>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminStats;
